import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer, Select, Space, Table, Alert, Spin, Empty, Button, Tag, Typography,
} from 'antd';
import dayjs from 'dayjs';
import api from '../../libs/api';
import {
  INK, NEUTRAL, UNDER, FAINT, fmtMoney, fmtPct,
} from './shared';

/**
 * Pick a saved scenario, read what is in it, then load it.
 *
 * Loading used to be a single button that replaced everything on screen with
 * the most recent save, sight unseen. Two problems with that: the most recent
 * save is not necessarily the one anyone wants, and replacing a screenful of
 * assumptions without showing what is arriving is a change nobody can consent
 * to. So: choose a version, see its note and the numbers it carries, and only
 * then confirm.
 *
 * A scenario is a version across every month of the window, not one month's
 * history — a save writes the whole window at once, and restoring half of an
 * agreed set of assumptions is not restoring it.
 */

/**
 * What to show for a scenario, per model.
 *
 * Each entity's drivers mean different things and there is no generic rendering
 * that reads well for both, so each says what its own numbers are. Unknown
 * shapes fall back to listing the raw keys rather than showing nothing —
 * a scenario saved by an older version of the model is still worth reading.
 */
const SHAPES = {
  // Keyed by the entity code the rest of the page uses. Keying it 'sh' meant
  // ShineHub missed and fell through to PW's shape, so its drawer asked a
  // scenario of systems and installs for sales and purchase orders and got
  // four columns of nothing.
  shinehub: {
    driverColumns: [
      { title: 'Sales', key: 'totalSales', render: (v) => v ?? '—' },
      { title: 'Installs', key: 'totalInstalls', render: (v) => v ?? '—' },
    ],
    assumptions: (a) => {
      if (!a?.byType) return null;
      return {
        rows: ['Upfront', 'PPA', 'BNPL'].map((t) => ({
          key: t,
          label: t,
          gpPct: a.byType[t]?.cogsPct != null ? (1 - a.byType[t].cogsPct) * 100 : null,
          avgRevenue: a.byType[t]?.avgRevenue,
        })),
        extra: [
          a.avgClosingToInstallDays != null && `closing → install lag ${a.avgClosingToInstallDays}d`,
          a.includeExisting != null && (a.includeExisting ? 'existing pipeline included' : 'new sales only'),
        ].filter(Boolean),
      };
    },
  },
  pw: {
    driverColumns: [
      { title: 'Sales', key: 'sales', render: fmtMoney },
      { title: 'Purchase A', key: 'purchaseA', render: fmtMoney },
      { title: 'Purchase B', key: 'purchaseB', render: fmtMoney },
      { title: 'Purchase C', key: 'purchaseC', render: fmtMoney },
    ],
    assumptions: (a) => (a?.cogsPct == null ? null : {
      rows: [],
      extra: [`cost of sales ${(a.cogsPct * 100).toFixed(1)}% of revenue`],
    }),
  },
};

export default function ScenarioDrawer({ entity, open, onClose, onLoad }) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/api/cashflow/scenarios', { params: { entity, kind: 'gp' } });
      const scenarios = r.data.data?.scenarios || [];
      setList({ months: r.data.data?.months || [], scenarios });
      // Open on the newest, which is what someone reaching for "load saved"
      // most often wants — but showing it rather than applying it.
      setPicked(scenarios[0]?.version ?? null);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not read the saved scenarios');
      setList(null);
    }
    setLoading(false);
  }, [entity]);

  useEffect(() => { if (open) load(); }, [open, load]);

  /**
   * No shape for an entity means no columns invented for it. Falling back to
   * another entity's shape produced a table that looked right and was entirely
   * empty, which is worse than one that says it cannot read this.
   */
  const shape = SHAPES[entity] || null;
  const scenario = list?.scenarios.find((s) => s.version === picked) || null;
  const months = list?.months || [];

  const body = () => {
    if (error) return <Alert type="error" showIcon message={error} />;
    if (!list) return <Empty description={loading ? 'Reading saved scenarios…' : 'No data'} />;
    if (!list.scenarios.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={(
            <span style={{ fontSize: 13 }}>
              Nothing saved for these months yet.
              <div style={{ color: NEUTRAL, fontSize: 12, marginTop: 6 }}>
                Save a forecast and it becomes version 1; every save after that is kept beside it.
              </div>
            </span>
          )}
        />
      );
    }

    if (!shape) {
      return <Alert type="warning" showIcon message={`No scenario layout for ${entity}.`} />;
    }

    const assumptions = scenario ? shape.assumptions(
      Object.values(scenario.byMonth)[0]?.assumptions,
    ) : null;

    return (
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <div style={{ fontSize: 12, color: NEUTRAL, marginBottom: 6 }}>Version</div>
          <Select
            style={{ width: '100%' }}
            value={picked}
            onChange={setPicked}
            options={list.scenarios.map((s) => ({
              value: s.version,
              label: `v${s.version} · ${dayjs(s.savedAt).format('D MMM YYYY HH:mm')}`
                + `${s.savedBy ? ` · ${s.savedBy}` : ''}`,
            }))}
          />
        </div>

        {scenario && (
          <>
            <div>
              <div style={{ fontSize: 12, color: NEUTRAL, marginBottom: 4 }}>Note</div>
              {scenario.note
                ? <Typography.Text style={{ fontSize: 13 }}>{scenario.note}</Typography.Text>
                : <span style={{ fontSize: 13, color: FAINT }}>No note was left with this save.</span>}
            </div>

            {assumptions && (
              <div>
                <div style={{ fontSize: 12, color: NEUTRAL, marginBottom: 6 }}>Assumptions</div>
                {assumptions.rows.length > 0 && (
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="key"
                    dataSource={assumptions.rows}
                    columns={[
                      { title: 'Type', dataIndex: 'label', width: 90 },
                      {
                        title: 'GP %', dataIndex: 'gpPct', width: 90, align: 'right',
                        render: (v) => (v == null ? '—' : fmtPct(v)),
                      },
                      {
                        title: 'Avg revenue / system', dataIndex: 'avgRevenue', align: 'right',
                        render: (v) => (v == null ? '—' : fmtMoney(v)),
                      },
                    ]}
                  />
                )}
                {assumptions.extra.map((x) => (
                  <Tag key={x} style={{ marginTop: 8 }}>{x}</Tag>
                ))}
              </div>
            )}

            <div>
              <div style={{ fontSize: 12, color: NEUTRAL, marginBottom: 6 }}>
                Drivers and what they produced
              </div>
              <Table
                size="small"
                pagination={false}
                rowKey="month"
                scroll={{ x: 460 }}
                dataSource={months.map((m) => ({
                  month: m,
                  ...(scenario.byMonth[m]?.drivers || {}),
                  outputs: scenario.byMonth[m]?.outputs || null,
                  missing: !scenario.byMonth[m],
                }))}
                columns={[
                  {
                    title: 'Month', dataIndex: 'month', width: 90, fixed: 'left',
                    render: (v, r) => (
                      <span style={{ color: r.missing ? FAINT : INK }}>
                        {dayjs(v).format('MMM YY')}
                      </span>
                    ),
                  },
                  ...shape.driverColumns.map((c) => ({
                    title: c.title,
                    dataIndex: c.key,
                    width: 110,
                    align: 'right',
                    render: (v, r) => (r.missing ? <span style={{ color: FAINT }}>—</span> : c.render(v)),
                  })),
                  {
                    key: 'revenue', title: 'Revenue', dataIndex: 'outputs', width: 120, align: 'right',
                    render: (o) => (o ? fmtMoney(o.revenue) : <span style={{ color: FAINT }}>—</span>),
                  },
                  {
                    key: 'gp', title: 'GP', dataIndex: 'outputs', width: 130, align: 'right',
                    render: (o) => (o ? (
                      <span style={{ color: UNDER }}>
                        {fmtMoney(o.gp)}
                        {o.revenue ? (
                          <span style={{ color: NEUTRAL, marginLeft: 6 }}>
                            {fmtPct((o.gp / o.revenue) * 100)}
                          </span>
                        ) : null}
                      </span>
                    ) : <span style={{ color: FAINT }}>—</span>),
                  },
                ]}
              />
              {/* A month the scenario never covered is left alone on load — it
                  is not the same as a month it forecast at zero. */}
              {months.some((m) => !scenario.byMonth[m]) && (
                <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 8 }}>
                  This scenario does not cover every month in the current window. The months it
                  has no entry for are left as they are rather than blanked.
                </div>
              )}
            </div>
          </>
        )}
      </Space>
    );
  };

  return (
    <Drawer
      title="Load a saved scenario"
      placement="right"
      width={620}
      open={open}
      onClose={onClose}
      extra={(
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            disabled={!scenario}
            onClick={() => { onLoad(scenario); onClose(); }}
          >
            Load v{scenario?.version ?? '—'}
          </Button>
        </Space>
      )}
    >
      <Spin spinning={loading}>{body()}</Spin>
    </Drawer>
  );
}
